import { Command } from 'commander';

import { graphqlUnauthenticated } from '../graphql/client.ts';
import { LogoutUrl } from '../graphql/operations/hermes.ts';
import { clearApplicationSession } from '../lib/application-session.ts';
import { clearCredentials } from '../lib/auth.ts';
import { openBrowser } from '../lib/browser.ts';
import { clearIdentity } from '../lib/config.ts';
import { hint, info, success, warn } from '../lib/output.ts';

export const logoutCommand = new Command('logout')
  .summary('Log out and remove stored credentials')
  .description(
    'Log out and remove stored credentials\n\n' +
      'Deletes the saved tokens from ~/.sortlist/credentials.json and\n' +
      'opens the Auth0 logout URL in your browser so the next `sl login`\n' +
      "doesn't silently reuse the previous account.\n\n" +
      'Examples:\n' +
      '  sl logout                   Clear credentials and log out of Auth0\n' +
      '  sl logout --no-browser      Only clear local credentials',
  )
  .option('--no-browser', 'Skip opening the Auth0 logout URL in a browser')
  .action(async function (this: Command) {
    const opts = this.optsWithGlobals();

    clearCredentials();
    clearIdentity();
    // Also the application session: "logged out" has to mean no working token of
    // any kind, and its token keeps authenticating until it expires on its own.
    const hadSession = clearApplicationSession();
    success('Logged out — credentials removed.');
    if (hadSession) info('The application session was cleared too.');

    if (!opts.browser) {
      hint('Run `sl login` to authenticate again.');
      return;
    }

    try {
      const data = await graphqlUnauthenticated(LogoutUrl, {}, opts.endpoint);
      if (data.logoutUrl) {
        openBrowser(data.logoutUrl);
        info('Opened Auth0 logout in your browser.');
      }
    } catch {
      warn('Could not fetch the Auth0 logout URL — skipped browser logout.');
    }

    hint('Run `sl login` to authenticate again.');
  });
